import { Fragment, useEffect, useState } from 'react';
import { Root } from '@radix-ui/react-dialog';
import styled from 'styled-components';
import tw from 'twin.macro';

import HouseImage from '@/assets/images/image-house.webp';
import useSalesRef from '@/api/mutations/useSalesRef';
import useUser from '@/api/queries/useUser';

import {
  Button,
  HomeTypeTagContainer,
  Dialog,
  LoadingOverlay,
  NoDataInfo,
  SoldTable,
  TableHeader,
  TooltipInfo,
  WeightChip
} from '@/components';
import AdvancedFilter from '../../components/AdvancedFilter';
import Filters from '@/components/Filters';
import { useMapContext } from '@/context/MapContext';
import { useFilterContext } from '@/context/FilterContext';
import { useProjectContext } from '@/context/ProjectContext';
import { IndexResponseType } from '@/types/apiTypes';
import {
  ButtonColor,
  ButtonSize,
  Icons,
  SearchParams,
  TabsType,
  OwnershipType,
  ColumnChoiceType,
  HomeTypeEnum
} from '@/enums';
import { filterItems, weightItems } from './filterObjects';
import { getMapMarkerDataFromSold } from '@/helpers';
import strings from '@/language/Swedish';
import {
  ContentContainer,
  WeightContainer,
  InnerContainer,
  TableContainer,
  TopRightWrapper,
  ButtonContainer,
  HideButton,
} from '../../styles/MapAttributesStyles';
import {
  CoordinatesType,
  SoldRefDataType,
  SoldWeightDataType,
} from '../../types/dataTypes';
import { ANALYTICS_ROLE, DISTANCE_STEPS_SOLD } from '../../constants';
import { getSoldApiParams } from '@/api/apiHelpers';
import { defaultCoordinates } from '@/context/default';
import useIsMobile from '@/helpers/useIsMobile';
const StyledSold = styled.div`
  ${tw`flex h-full overflow-hidden`};
`;



const Sold = () => {
  const [isResetDialogOpen, setIsResetDialogOpen] = useState<boolean>(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState<boolean>(false);
  const [isWeightChipOpen, setIsWeightChipOpen] = useState<boolean>(false);
  const [distanceStep, setDistanceStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [init, setInit] = useState(true); // Prevent rerender cause of cordinates is set several times when project is loading (4 times) refactor in future
  const { data: userData } = useUser();
  const isMobile = useIsMobile();
  const hasAnalyticsRole = userData
    ? userData.roles.includes(ANALYTICS_ROLE)
    : false;

  const {
    currentCoordinates,
    isStreetViewOn,
    isMapVisible,
    setCoordinates,
    setSoldMarkerObjects,
  } = useMapContext();

  const {
    project: {
      soldTableColumns,
      soldTableIds,
      referencePrice,
      soldTableData,
      soldSummary,
      soldPolygons,
      own_choice_reference_fields,
      indexValues

    },
    updateSoldTableData,
    setProject,
    isAdvancedFilterOpen,
    toggleColumnVisibility,
    addExcludedTableIds,
    columnOption,
    setColumnOption,
  } = useProjectContext();

  const {
    soldData: soldFilterData,
    isFilterHidden,
    setIsFilterHidden,
    setSoldParam,
    resetSoldAndAdParams,
    setMarketParam,
    setSoldAndAdParam,
  } = useFilterContext();

  const params = getSoldApiParams(soldFilterData);

  const { mutate: updateSoldDataMutation, data: searchData, isError: isError, isPending: isSearchPending } = useSalesRef();

  function refetch(step: number) {
    updateSoldDataMutation({
      coordinates: currentCoordinates,
      parameters: params,
      soldTableIds: soldTableIds,
      polygonCoordinates: soldPolygons,
      distance: DISTANCE_STEPS_SOLD[step]
    });
  }

  useEffect(() => {
    if (!isAdvancedFilterOpen) {
      if ((soldFilterData.filterData && !soldFilterData?.filterData?.ownership_type?.includes(OwnershipType.coOperativeApartment) && soldFilterData?.filterData?.ownership_type)) {
        setColumnOption(ColumnChoiceType.littleHouse);
      } else if (soldFilterData.filterData && (soldFilterData?.filterData?.ownership_type?.includes(OwnershipType.coOperativeApartment)) || !soldFilterData?.filterData?.ownership_type) {
        setColumnOption(ColumnChoiceType.coOperative);
      }
      const updatedIndexValues: IndexResponseType = {
        ...indexValues as IndexResponseType,
        home_type: ((soldFilterData?.filterData?.home_type?.includes(HomeTypeEnum.house) || !soldFilterData?.filterData?.home_type) && !soldFilterData?.filterData?.ownership_type?.includes(OwnershipType.coOperativeApartment) && soldFilterData?.filterData?.ownership_type) ? HomeTypeEnum.house : HomeTypeEnum.apartment
      }
      setProject((prevState) => ({
        ...prevState,
        indexValues: updatedIndexValues
      }));
    }
  }, [isAdvancedFilterOpen]);

  useEffect(() => {
    if (!isAdvancedFilterOpen && !isFilterDialogOpen && !isWeightChipOpen && !init) {
      setDistanceStep(0);
      setLoading(true);
      refetch(0);
    }
  }, [isAdvancedFilterOpen, isFilterDialogOpen, soldFilterData, isWeightChipOpen]);

  useEffect(() => {
    if (init && currentCoordinates !== defaultCoordinates) {
      refetch(0);
      setInit(false);
    }
    else if (!init && currentCoordinates !== defaultCoordinates) {
      setDistanceStep(0);
      setLoading(true);
      refetch(0);
    }
  }, [currentCoordinates, soldPolygons]);

  useEffect(() => {
    if (!init && (searchData?.sales?.length || 0) < (soldFilterData.filterData.limit * 0.8) && distanceStep < DISTANCE_STEPS_SOLD.length - 1 && !isSearchPending) {
      refetch(distanceStep + 1);
      setDistanceStep((prevState) => prevState + 1);
    } else if (searchData) {
      updateSoldTableData(searchData);

      if (searchData.sales.length > 0) {
        const mapMarkerData = getMapMarkerDataFromSold(searchData.sales);

        setSoldMarkerObjects(mapMarkerData);
      } else {
        setSoldMarkerObjects(null);
      }
      setLoading(false);
      setInit(false);
    }

  }, [searchData]);

  useEffect(() => {
    if (!init) {
      setDistanceStep(0);
      setLoading(true);
      refetch(0);
    }
  }, [soldTableIds.excluded]);

  const handleWeightChange = (keyEl: string, value: number) => {
    setSoldAndAdParam(SearchParams.weight, keyEl, value);
  };

  const handleRefValueChange = (keyEl: string, value: number) => {
    setSoldAndAdParam(SearchParams.ref, keyEl, value);
  };

  const handleLimitChange = (value: string | number) => {
    const limitValue = parseInt(value.toString());
    setSoldAndAdParam(SearchParams.filter, 'limit', limitValue);
  };

  const handleAdvancedSearch = (coordinates?: CoordinatesType) => {
    if (coordinates) setCoordinates(coordinates);
  };

  return (
    <StyledSold>
      <AdvancedFilter
        handleSearch={handleAdvancedSearch}
        filterData={soldFilterData}
        setMarketParam={setMarketParam}
        setMultipleParam={setSoldAndAdParam}
        handleResetFilter={() => resetSoldAndAdParams(SearchParams.advancedFilter)}
      />
      <>
        <WeightContainer $isHidden={isFilterHidden || isStreetViewOn || isMobile}>
            <InnerContainer>
              <div className="hidden sm:flex w-full flex-wrap gap-1">
                {weightItems.map((item, index) => (
                  <Fragment key={item.keyWeightEl}>
                    <WeightChip
                      setIsWeightChipOpen={setIsWeightChipOpen}
                      weightValue={
                        soldFilterData.weightData[
                        item.keyWeightEl as keyof SoldWeightDataType
                        ]
                      }
                      refValue={
                        soldFilterData.refData[
                        item.keyRefEl as keyof SoldRefDataType
                        ] || undefined
                      }
                      icon={item.icon}
                      inputType={item.inputType}
                      inputMin={item.min}
                      inputMax={item.max}
                      keyWeightEl={item.keyWeightEl}
                      keyRefEl={item.keyRefEl}
                      label={item.label}
                      unit={item.unit}
                      onUpdateWeight={handleWeightChange}
                      onUpdateRefValue={handleRefValueChange}
                    />
                    {index % 4 === 3 && <div className="basis-full"></div>}
                  </Fragment>
                ))}
                <Button
                  icon={Icons.refresh}
                  color={ButtonColor.light}
                  size={ButtonSize.small}
                  onClick={() => setIsResetDialogOpen(true)}
                >
                  {strings.data.reset}
                </Button>
              </div>
              <Root open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
                <Dialog
                  title={`${strings.dashboard.resetWarningHeading}`}
                  description={`${strings.dashboard.resetWarning}`}
                  bottomButton={{
                    text: strings.dashboard.resetWarningButton,
                    type: 'submit',
                    onClick: () => resetSoldAndAdParams(SearchParams.weightRef),
                  }}
                />
              </Root>
            </InnerContainer>
          </WeightContainer>
          <TopRightWrapper>
            <ButtonContainer>
              <Filters
                setCoordinates={setCoordinates}
                setIsOpen={setIsFilterDialogOpen}
                handleResetFilter={() => resetSoldAndAdParams(SearchParams.filter)}
                handleClose={() => {
                  refetch(0);
                }}
                filterData={soldFilterData.filterData}
                setParam={setSoldParam}
                setMultipleParam={setSoldAndAdParam}
                filterItems={filterItems}
                isOpen={isFilterDialogOpen}
                isFilterHidden={isFilterHidden}
              />
              <TooltipInfo content={strings.dashboard.hide}>
                <div>
                  <HideButton
                    icon={Icons.arrowUp}
                    color={isFilterHidden ? ButtonColor.primary : ButtonColor.light}
                    size={ButtonSize.large}
                    $isHidden={isFilterHidden}
                    onClick={() => setIsFilterHidden((prevState) => !prevState)}
                  />
                </div>
              </TooltipInfo>
            </ButtonContainer>
            <HomeTypeTagContainer filterData={soldFilterData.filterData} />
          </TopRightWrapper>
        </>
        <ContentContainer $isFilterHidden={isFilterHidden} $isMapVisible={isMapVisible}>
          {loading && <LoadingOverlay />}
          {!isError && soldTableData ? (
            <TableContainer>
              <TableHeader
                limit={soldFilterData.filterData.limit}
                hasExcludedIds={
                  soldTableIds.excluded && soldTableIds.excluded.size > 0
                    ? true
                    : false
                }
                own_choice_reference_fields={own_choice_reference_fields}
                nrOfObjects={soldTableData.length}
                nrOfSelectedObjects={soldTableIds.selected?.size || 0}
                userRefSqm={soldFilterData.refData.living_area_reference}
                referencePrice={referencePrice}
                columns={soldTableColumns}
                isAuthorized={hasAnalyticsRole}
                hasColumnOption
                handleLimitChange={handleLimitChange}
                setColumnOption={setColumnOption}
                columnOption={columnOption}
                setColumns={(columns) =>
                  setProject((prevState) => ({
                    ...prevState,
                    soldTableColumns: columns,
                    soldTableReportColumns: columns,
                  }))
                }
                resetExcludedIds={() =>
                  setProject((prevState) => ({
                    ...prevState,
                    soldTableIds: { ...prevState.soldTableIds, excluded: null },
                  }))
                }
                toggleColumns={(id: string) =>
                  toggleColumnVisibility(TabsType.sold, id)
                }
              />
              <SoldTable
                tableData={soldTableData}
                tableSummary={soldSummary}
                updateExcluded={(type, id) => {
                  addExcludedTableIds(type, id);
                }}
              />
            </TableContainer>
          ) : (
            <NoDataInfo
              heading={
                isError
                  ? strings.data.somethingWentWrong
                  : strings.table.noDataHeading
              }
              altText={strings.table.noDataHeading}
              text={isError ? strings.data.error : strings.table.noDataText}
              img={HouseImage}
            />
          )}
        </ContentContainer>
      </StyledSold>
  );
};

export default Sold;
